import type { ReactNode } from 'react';
import { Link, type To } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

export type TextLinkColor =
    | 'primary'
    | 'secondary'
    | 'neutral'
    | 'success'
    | 'warning'
    | 'error'
    | 'inherit';

interface TextLinkCommonProps {
    /** Colour of the text or icon. `inherit` takes the surrounding text colour. */
    color: TextLinkColor;
    children?: ReactNode;
    /** Icon shown before the children. With no children, pass `ariaLabel`. */
    startIcon?: ReactNode;
    ariaLabel?: string;
    className?: string;
}

interface RouterDestination {
    to: To;
    state?: unknown;
    href?: never;
    target?: never;
    rel?: never;
    onClick?: never;
    disabled?: never;
}

interface ExternalDestination {
    href: string;
    target?: '_blank';
    rel?: string;
    to?: never;
    state?: never;
    onClick?: never;
    disabled?: never;
}

interface Action {
    onClick: () => void;
    disabled?: boolean;
    to?: never;
    state?: never;
    href?: never;
    target?: never;
    rel?: never;
}

export type TextLinkProps = TextLinkCommonProps &
    (RouterDestination | ExternalDestination | Action);

const BASE_CLASSES = 'cursor-pointer underline focus-visible:outline-2 focus-visible:outline-offset-2';
const ICON_LAYOUT_CLASSES = 'inline-flex items-center gap-2';
const DISABLED_CLASSES = 'disabled:cursor-not-allowed disabled:opacity-50';

const COLOR_CLASSES: Record<TextLinkColor, string> = {
    primary: 'text-mp-link',
    secondary: 'text-mp-muted',
    neutral: 'text-mp-foreground',
    success: 'text-mp-success',
    warning: 'text-mp-warning',
    error: 'text-mp-error',
    inherit: 'text-inherit',
};

export function TextLink(props: TextLinkProps) {
    const { color, children, startIcon, ariaLabel, className } = props;
    const classes = twMerge(
        BASE_CLASSES,
        startIcon ? ICON_LAYOUT_CLASSES : '',
        COLOR_CLASSES[color],
        className,
    );
    const content = (
        <>
            {startIcon && <span className="flex items-center">{startIcon}</span>}
            {children}
        </>
    );

    if (props.to !== undefined) {
        return (
            <Link to={props.to} state={props.state} aria-label={ariaLabel} className={classes}>
                {content}
            </Link>
        );
    }
    if (props.href !== undefined) {
        return (
            <a
                href={props.href}
                target={props.target}
                rel={props.rel}
                aria-label={ariaLabel}
                className={classes}
            >
                {content}
            </a>
        );
    }
    return (
        <button
            type="button"
            onClick={props.onClick}
            disabled={props.disabled}
            aria-label={ariaLabel}
            className={twMerge(classes, DISABLED_CLASSES)}
        >
            {content}
        </button>
    );
}
